import React, { useState, useRef, useEffect, useMemo } from 'react';
import { CaretDown, MagnifyingGlass } from '@phosphor-icons/react';

// Country list with phone codes & flag emojis. MX/US/CA first; rest alphabetical.
const COUNTRIES = [
    { code: 'MX', name: 'México', dial: '+52', flag: '🇲🇽' },
    { code: 'US', name: 'Estados Unidos', dial: '+1', flag: '🇺🇸' },
    { code: 'CA', name: 'Canadá', dial: '+1', flag: '🇨🇦' },
    { code: 'AR', name: 'Argentina', dial: '+54', flag: '🇦🇷' },
    { code: 'AU', name: 'Australia', dial: '+61', flag: '🇦🇺' },
    { code: 'AT', name: 'Austria', dial: '+43', flag: '🇦🇹' },
    { code: 'BE', name: 'Bélgica', dial: '+32', flag: '🇧🇪' },
    { code: 'BO', name: 'Bolivia', dial: '+591', flag: '🇧🇴' },
    { code: 'BR', name: 'Brasil', dial: '+55', flag: '🇧🇷' },
    { code: 'CL', name: 'Chile', dial: '+56', flag: '🇨🇱' },
    { code: 'CN', name: 'China', dial: '+86', flag: '🇨🇳' },
    { code: 'CO', name: 'Colombia', dial: '+57', flag: '🇨🇴' },
    { code: 'CR', name: 'Costa Rica', dial: '+506', flag: '🇨🇷' },
    { code: 'CU', name: 'Cuba', dial: '+53', flag: '🇨🇺' },
    { code: 'DE', name: 'Alemania', dial: '+49', flag: '🇩🇪' },
    { code: 'DK', name: 'Dinamarca', dial: '+45', flag: '🇩🇰' },
    { code: 'DO', name: 'República Dominicana', dial: '+1', flag: '🇩🇴' },
    { code: 'EC', name: 'Ecuador', dial: '+593', flag: '🇪🇨' },
    { code: 'EG', name: 'Egipto', dial: '+20', flag: '🇪🇬' },
    { code: 'ES', name: 'España', dial: '+34', flag: '🇪🇸' },
    { code: 'FI', name: 'Finlandia', dial: '+358', flag: '🇫🇮' },
    { code: 'FR', name: 'Francia', dial: '+33', flag: '🇫🇷' },
    { code: 'GB', name: 'Reino Unido', dial: '+44', flag: '🇬🇧' },
    { code: 'GR', name: 'Grecia', dial: '+30', flag: '🇬🇷' },
    { code: 'GT', name: 'Guatemala', dial: '+502', flag: '🇬🇹' },
    { code: 'HN', name: 'Honduras', dial: '+504', flag: '🇭🇳' },
    { code: 'IE', name: 'Irlanda', dial: '+353', flag: '🇮🇪' },
    { code: 'IN', name: 'India', dial: '+91', flag: '🇮🇳' },
    { code: 'IT', name: 'Italia', dial: '+39', flag: '🇮🇹' },
    { code: 'JP', name: 'Japón', dial: '+81', flag: '🇯🇵' },
    { code: 'NI', name: 'Nicaragua', dial: '+505', flag: '🇳🇮' },
    { code: 'NL', name: 'Países Bajos', dial: '+31', flag: '🇳🇱' },
    { code: 'NO', name: 'Noruega', dial: '+47', flag: '🇳🇴' },
    { code: 'NZ', name: 'Nueva Zelanda', dial: '+64', flag: '🇳🇿' },
    { code: 'PA', name: 'Panamá', dial: '+507', flag: '🇵🇦' },
    { code: 'PE', name: 'Perú', dial: '+51', flag: '🇵🇪' },
    { code: 'PL', name: 'Polonia', dial: '+48', flag: '🇵🇱' },
    { code: 'PR', name: 'Puerto Rico', dial: '+1', flag: '🇵🇷' },
    { code: 'PT', name: 'Portugal', dial: '+351', flag: '🇵🇹' },
    { code: 'PY', name: 'Paraguay', dial: '+595', flag: '🇵🇾' },
    { code: 'SE', name: 'Suecia', dial: '+46', flag: '🇸🇪' },
    { code: 'SV', name: 'El Salvador', dial: '+503', flag: '🇸🇻' },
    { code: 'CH', name: 'Suiza', dial: '+41', flag: '🇨🇭' },
    { code: 'UY', name: 'Uruguay', dial: '+598', flag: '🇺🇾' },
    { code: 'VE', name: 'Venezuela', dial: '+58', flag: '🇻🇪' },
];

/**
 * Phone input with country picker.
 * Outputs E.164-ish formatted phone string: "+52 5512345678"
 */
const PhoneWithCountryPicker = ({ value = '', onChange, compact = false, ...rest }) => {
    const [country, setCountry] = useState(COUNTRIES[0]); // MX default
    const [phone, setPhone] = useState('');
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const wrapRef = useRef(null);
    const h = compact ? 'h-10' : 'h-11';

    // Initial sync from external value (parse "+52 5512345678")
    useEffect(() => {
        if (!value) return;
        // Try to detect country code
        const match = COUNTRIES.find(c => value.startsWith(c.dial));
        if (match) {
            setCountry(match);
            setPhone(value.slice(match.dial.length).trim());
        } else {
            setPhone(value);
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const emit = (newCountry, newPhone) => {
        if (!newPhone) {
            onChange('');
        } else {
            onChange(`${newCountry.dial} ${newPhone}`);
        }
    };

    const filtered = useMemo(() => {
        if (!search) return COUNTRIES;
        const s = search.toLowerCase();
        return COUNTRIES.filter(
            c => c.name.toLowerCase().includes(s) ||
                 c.code.toLowerCase().includes(s) ||
                 c.dial.includes(s)
        );
    }, [search]);

    return (
        <div className="grid grid-cols-[auto_1fr] gap-1 relative" ref={wrapRef}>
            {/* Country chip / button */}
            <button
                type="button"
                onClick={() => setOpen(!open)}
                data-testid="phone-country-toggle"
                style={{ backgroundColor: '#FFFFFF' }}
                className={`${h} px-2.5 flex items-center gap-1 rounded-md border border-[#ECE3D2] hover:bg-[#FAF6F0] transition-colors`}
            >
                <span className={compact ? 'text-base leading-none' : 'text-xl leading-none'}>{country.flag}</span>
                <span className="text-xs font-medium text-[#1F2D4B] font-mono">{country.dial}</span>
                <CaretDown size={10} weight="bold" className="text-[#8B8474]" />
            </button>

            {/* Number input */}
            <input
                {...rest}
                type="tel"
                value={phone}
                onChange={(e) => {
                    const v = e.target.value.replace(/[^\d\s\-()]/g, '');
                    setPhone(v);
                    emit(country, v);
                }}
                placeholder="555 123 4567"
                style={{ backgroundColor: '#FFFFFF' }}
                className={`${h} rounded-md border border-[#ECE3D2] px-3 text-sm text-[#1F2D4B] focus:outline-none focus:ring-1 focus:ring-[#1F2D4B] focus:border-[#1F2D4B] placeholder:text-[#9CA3AF]`}
                data-testid="phone-number-input"
            />

            {open && (
                <div className="absolute top-full left-0 mt-1 z-50 w-80 bg-white border border-[#ECE3D2] rounded-md shadow-xl overflow-hidden">
                    <div className="p-2 border-b border-[#ECE3D2]">
                        <div className="relative">
                            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8B8474]" />
                            <input
                                autoFocus
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Buscar país..."
                                className="w-full h-9 pl-8 pr-3 rounded border border-[#ECE3D2] bg-white text-sm text-[#1F2D4B] focus:outline-none focus:ring-1 focus:ring-[#1F2D4B]"
                                data-testid="phone-country-search"
                            />
                        </div>
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                        {filtered.map((c) => (
                            <button
                                type="button"
                                key={c.code}
                                onClick={() => {
                                    setCountry(c);
                                    emit(c, phone);
                                    setOpen(false);
                                    setSearch('');
                                }}
                                className={`w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-[#F8F4EC] transition-colors text-left ${
                                    c.code === country.code ? 'bg-[#FAF6F0]' : ''
                                }`}
                                data-testid={`phone-country-option-${c.code}`}
                            >
                                <span className="text-xl leading-none">{c.flag}</span>
                                <span className="flex-1 text-[#1F2D4B] font-medium">{c.name}</span>
                                <span className="text-[#8B8474] font-mono text-xs">{c.dial}</span>
                            </button>
                        ))}
                        {filtered.length === 0 && (
                            <div className="px-3 py-4 text-sm text-[#8B8474] text-center">
                                Sin resultados
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PhoneWithCountryPicker;
